import React, { Suspense, lazy, useEffect } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Lazy-load pages
const InternalLoginPage = lazy(() => import('./pages/InternalLoginPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
import PartnerLayout from './layouts/PartnerLayout';
import AdminLayout from './layouts/AdminLayout';
import PartnerDashboard from './pages/partner/Dashboard';
import RegistrationWizard from './pages/partner/RegistrationWizard';
const PartnerVisitingCard = lazy(() => import('./pages/partner/VisitingCard'));
const PartnerProfile = lazy(() => import('./pages/partner/Profile'));
const PartnerNotifications = lazy(() => import('./pages/partner/Notifications'));
const PartnerPassbook = lazy(() => import('./pages/partner/Passbook'));
const PartnerCalculator = lazy(() => import('./pages/partner/Calculator'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminPartners = lazy(() => import('./pages/admin/Partners'));
const AdminCommissions = lazy(() => import('./pages/admin/Commissions'));
const AdminTiers = lazy(() => import('./pages/admin/Tiers'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const AdminAuditLogs = lazy(() => import('./pages/admin/AuditLogs'));
const AdminLeads = lazy(() => import('./pages/admin/Leads'));
const CommissionApprovals = lazy(() => import('./pages/admin/CommissionApprovals'));
const CommissionCardRate = lazy(() => import('./pages/partner/Calculator'));
const PayoutsPage = lazy(() => import('./pages/admin/Payouts'));
const EmailTemplates = lazy(() => import('./pages/admin/EmailTemplates'));
const ReportsPage = lazy(() => import('./pages/admin/Reports'));
const ApprovalWorkflows = lazy(() => import('./pages/admin/ApprovalWorkflows'));
const DiagnosticLogs = lazy(() => import('./pages/admin/DiagnosticLogs'));
const UserManagementPage = lazy(() => import('./pages/admin/UserManagement'));
const AdminBanks = lazy(() => import('./pages/admin/Banks'));
const AdminLoanProducts = lazy(() => import('./pages/admin/Products'));
const AdminLoanTypes = lazy(() => import('./pages/admin/LoanTypes'));
const AdminMatrix = lazy(() => import('./pages/admin/Matrix'));

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" />;
  if (roles?.length && !roles.some(r => user.roles?.includes(r))) {
    // If user is an internal user, redirect to admin dashboard instead of home
    const isInternalUser = INTERNAL_ROLES.some(r => user.roles?.includes(r));
    return <Navigate to={isInternalUser ? "/admin" : "/"} replace />;
  }
  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  roles: PropTypes.arrayOf(PropTypes.string),
}

// Protect registration page - redirect admin/internal users to admin dashboard
function PartnerOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  // If user has admin/internal roles, redirect to admin dashboard
  if (user && INTERNAL_ROLES.some(r => user.roles?.includes(r))) {
    return <Navigate to="/admin" />;
  }
  return children;
}

PartnerOnlyRoute.propTypes = {
  children: PropTypes.node.isRequired,
}

function LoadingSpinner() {
  return (
    <div className=" flex items-center justify-center min-h-screen bg-slate-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-[#0066CC] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-500 font-medium">Loading...</p>
      </div>
    </div>
  );
}

const INTERNAL_ROLES = ['super_admin', 'admin', 'relationship_manager', 'finance', 'accounts_team', 'compliance'];

// Zoho SalesIQ Chat Widget - Temporarily Disabled
/*
function hideZohoWidget(user) {
  const selectors = ['.zsiq_floatmain', '.zls-sptwndw', '#zsiq_float', '.zsiq_cnt', '.zsiq_theme1', '[id^="siq"]'];
  selectors.forEach(sel => {
    document.querySelectorAll(sel).forEach(el => {
      el.style.display = user ? 'none' : '';
    });
  });
}
*/

function AppRoutes() {
  const { user } = useAuth();

  // Zoho SalesIQ Chat Widget - Temporarily Disabled
  /*
  // Hide Zoho SalesIQ chat widget when user is logged in
  useEffect(() => {
    const onMutation = () => hideZohoWidget(user);
    onMutation();

    // Widget loads async, so observe DOM for when it appears
    const observer = new MutationObserver(onMutation);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [user]);
  */

  return (
    <Routes>
        {/* Public */}
        <Route path="/" element={<Suspense fallback={null}><LandingPage /></Suspense>} />
        <Route path="/login" element={<Suspense fallback={null}><LandingPage /></Suspense>} />
        <Route path="/register" element={<Suspense fallback={null}><LandingPage /></Suspense>} />
        <Route path="/internal/login" element={user ? <Navigate to="/admin" /> : <Suspense fallback={<LoadingSpinner />}><InternalLoginPage /></Suspense>} />
        <Route path="/auth/microsoft/callback" element={<Suspense fallback={<LoadingSpinner />}><InternalLoginPage /></Suspense>} />
        <Route path="/forgot-password" element={<Suspense fallback={<LoadingSpinner />}><ForgotPasswordPage /></Suspense>} />
        <Route path="/reset-password" element={<Suspense fallback={<LoadingSpinner />}><ResetPasswordPage /></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<LoadingSpinner />}><PrivacyPage /></Suspense>} />
        <Route path="/terms" element={<Suspense fallback={<LoadingSpinner />}><TermsPage /></Suspense>} />

        {/* Partner Portal */}
        <Route path="/partner" element={
          <ProtectedRoute roles={['partner_admin', 'partner_user']}>
            <PartnerLayout />
          </ProtectedRoute>
        }>
          <Route index element={<PartnerDashboard />} />
          <Route path="register" element={<PartnerOnlyRoute><RegistrationWizard /></PartnerOnlyRoute>} />
          <Route path="profile" element={<PartnerProfile />} />
          <Route path="visiting-card" element={<PartnerVisitingCard />} />
          <Route path="passbook" element={<PartnerPassbook />} />
          <Route path="calculator" element={<PartnerCalculator />} />
          <Route path="notifications" element={<PartnerNotifications />} />
        </Route>

        {/* Admin / Internal Portal */}
        <Route path="/admin" element={
          <ProtectedRoute roles={[...INTERNAL_ROLES]}>
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={<AdminDashboard />} />
          
          {/* Partner Management - RM + Admin */}
          <Route path="partners" element={
            <ProtectedRoute roles={['super_admin', 'admin', 'relationship_manager']}>
              <AdminPartners />
            </ProtectedRoute>
          } />
          <Route path="leads" element={
            <ProtectedRoute roles={['super_admin', 'admin', 'relationship_manager']}>
              <AdminLeads />
            </ProtectedRoute>
          } />
          
          {/* Configuration Pages - Admin Only */}
          <Route path="loan-products" element={
            <ProtectedRoute roles={['super_admin', 'admin']}>
              <AdminLoanProducts />
            </ProtectedRoute>
          } />
          <Route path="loan-types" element={
            <ProtectedRoute roles={['super_admin', 'admin']}>
              <AdminLoanTypes />
            </ProtectedRoute>
          } />
          {/* Legacy path — roots live under Loan Products */}
          <Route path="products" element={<Navigate to="/admin/loan-products" replace />} />
          <Route path="banks" element={
            <ProtectedRoute roles={['super_admin', 'admin']}>
              <AdminBanks />
            </ProtectedRoute>
          } />
          <Route path="tiers" element={
            <ProtectedRoute roles={['super_admin', 'admin']}>
              <AdminTiers />
            </ProtectedRoute>
          } />
          <Route path="commissions" element={
            <ProtectedRoute roles={['super_admin', 'admin']}>
              <AdminCommissions />
            </ProtectedRoute>
          } />
          <Route path="matrix" element={
            <ProtectedRoute roles={['super_admin', 'admin']}>
              <AdminMatrix />
            </ProtectedRoute>
          } />
          <Route path="email-templates" element={
            <ProtectedRoute roles={['super_admin', 'admin']}>
              <EmailTemplates />
            </ProtectedRoute>
          } />
          <Route path="approval-workflows" element={
            <ProtectedRoute roles={['super_admin', 'admin']}>
              <ApprovalWorkflows />
            </ProtectedRoute>
          } />
          
          {/* Financial Pages - Finance + Admin (excludes accounts_team and RM) */}
          <Route path="commission-approvals" element={
            <ProtectedRoute roles={['super_admin', 'admin', 'finance']}>
              <CommissionApprovals />
            </ProtectedRoute>
          } />
          <Route path="payouts" element={
            <ProtectedRoute roles={['super_admin', 'admin', 'finance', 'accounts_team']}>
              <PayoutsPage />
            </ProtectedRoute>
          } />
          <Route path="reports" element={
            <ProtectedRoute roles={['super_admin', 'admin', 'finance', 'accounts_team']}>
              <ReportsPage />
            </ProtectedRoute>
          } />
          
          {/* Commission Calculator - excludes accounts_team */}
          <Route path="commission-card" element={
            <ProtectedRoute roles={['super_admin', 'admin', 'finance', 'relationship_manager']}>
              <CommissionCardRate />
            </ProtectedRoute>
          } />
          
          {/* Settings - Admin Only (excludes accounts_team) */}
          <Route path="settings" element={
            <ProtectedRoute roles={['super_admin', 'admin']}>
              <AdminSettings />
            </ProtectedRoute>
          } />
          
          {/* Audit Logs - excludes accounts_team */}
          <Route path="audit-logs" element={
            <ProtectedRoute roles={['super_admin', 'admin', 'finance', 'compliance']}>
              <AdminAuditLogs />
            </ProtectedRoute>
          } />
          
          {/* User Management - Admin Only */}
          <Route path="users" element={
            <ProtectedRoute roles={['super_admin', 'admin']}>
              <UserManagementPage />
            </ProtectedRoute>
          } />
          
          {/* Team Management - Admin Only */}
          <Route path="team" element={
            <ProtectedRoute roles={['super_admin', 'admin']}>
              <UserManagementPage />
            </ProtectedRoute>
          } />
          <Route path="accounts-team" element={
            <ProtectedRoute roles={['super_admin', 'admin']}>
              <UserManagementPage />
            </ProtectedRoute>
          } />
          
          {/* Diagnostics - Super Admin Only */}
          <Route path="diagnostics" element={
            <ProtectedRoute roles={['super_admin']}>
              <DiagnosticLogs />
            </ProtectedRoute>
          } />
        </Route>

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppContent() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" />
      </AuthProvider>
    </Router>
  );
}

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (googleClientId) {
    return (
      <GoogleOAuthProvider clientId={googleClientId}>
        <AppContent />
      </GoogleOAuthProvider>
    );
  }
  return <AppContent />;
}

export default App;
